import type { NavItem } from './navigation.api';

export type NavItemType = 'custom' | 'page' | 'post' | 'destination' | 'category';

export interface FlatDraftItem {
  id: string;
  label: string;
  href: string | null;
  pageId: string | null;
  openInNewTab: boolean;
  isVisible: boolean;
  depth: number;
  itemType: NavItemType;
  isNew?: boolean;
  isDeleted?: boolean;
}

export const inferNavItemType = (item: Pick<NavItem, 'pageId' | 'href'>): NavItemType => {
  if (item.pageId) return 'page';
  const href = item.href ?? '';
  if (href.startsWith('/destinations/')) return 'destination';
  if (href.startsWith('/categories/')) return 'category';
  const pathOnly = href.split('?')[0] ?? '';
  const parts = pathOnly.split('/').filter(Boolean);
  if (parts.length === 2 && parts[0] !== 'blogs' && parts[0] !== 'package-itinerary') {
    return 'destination';
  }
  if (href.startsWith('/blogs/') && href.split('/').filter(Boolean).length === 2) return 'post';
  return 'custom';
};

/** Slug/path is owned by the CMS record - not editable in the menu builder. */
export const isNavHrefLocked = (item: Pick<FlatDraftItem, 'itemType' | 'pageId'>): boolean =>
  item.pageId !== null ||
  item.itemType === 'destination' ||
  item.itemType === 'category' ||
  item.itemType === 'page';

export const navItemTypeLabel: Record<NavItemType, string> = {
  page: 'Page',
  post: 'Post',
  destination: 'Destination',
  category: 'Category Page',
  custom: 'Custom Link',
};

export const navHrefLockHint: Record<NavItemType, string | null> = {
  page: 'URL follows the page slug from Pages.',
  destination: 'URL follows the slug from Destination Pages.',
  category: 'URL follows the slug from Category Pages.',
  post: null,
  custom: null,
};

/** Nested API tree → flat list with depth (pre-order). */
export const treeToFlat = (items: NavItem[], depth = 0): FlatDraftItem[] => {
  const out: FlatDraftItem[] = [];
  for (const item of items) {
    out.push({
      id: item.id,
      label: item.label,
      href: item.href,
      pageId: item.pageId,
      openInNewTab: item.openInNewTab,
      isVisible: item.isVisible,
      depth,
      itemType: inferNavItemType(item),
    });
    if (item.children.length > 0) {
      out.push(...treeToFlat(item.children, depth + 1));
    }
  }
  return out;
};

/** Derive parentId + sortOrder per sibling group from a flat depth-ordered list. */
export const flatToReorderPayload = (
  items: FlatDraftItem[],
): { id: string; parentId: string | null; sortOrder: number }[] => {
  const active = items.filter((i) => !i.isDeleted);
  const stack: { id: string; depth: number }[] = [];
  const siblingCount = new Map<string | null, number>();
  const out: { id: string; parentId: string | null; sortOrder: number }[] = [];

  for (const item of active) {
    while (stack.length > 0 && stack[stack.length - 1]!.depth >= item.depth) {
      stack.pop();
    }
    const parentId = stack.length > 0 ? stack[stack.length - 1]!.id : null;
    const order = siblingCount.get(parentId) ?? 0;
    siblingCount.set(parentId, order + 1);
    out.push({ id: item.id, parentId, sortOrder: order });
    stack.push({ id: item.id, depth: item.depth });
  }

  return out;
};

export const newTempId = (): string => `temp-${crypto.randomUUID()}`;

export const clampDepth = (items: FlatDraftItem[], index: number, nextDepth: number): number => {
  const max = index > 0 ? items[index - 1]!.depth + 1 : 0;
  return Math.max(0, Math.min(nextDepth, max));
};
